import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Button, CircularProgress, IconButton, TextField } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faTrash } from "@fortawesome/pro-solid-svg-icons";

import type { IPartner, IHoliday, IHolidaySchedule } from "App/Modules/Models";
import type { IPartnersRepository } from "App/Modules/Repositories";
import "./_holiday-schedule.scss";

type HolidayScheduleDialogProps = {
    onClose: () => void;
    partner: IPartner;
    partnersRepository: IPartnersRepository;
    onHolidayScheduleChange: () => void;
};

// Create a new type based on IHolidaySchedule, where id is optional
type NewTypeHolidaySchedule = Omit<IHolidaySchedule, "id"> & {
    id?: string;
};

type EditableHoliday = Omit<IHoliday, "id"> & {
    id?: string;
    hasEndDate?: boolean;
};

export const HolidayScheduleDialog = (props: HolidayScheduleDialogProps) => {
    const { t } = useTranslation();
    const localNow = moment(new Date());
    const { onClose, partner, partnersRepository } = props;

    const [errors, setErrors] = useState([]);
    const [saveError, setSaveError] = useState(false);

    const [currentHolidaySchedule] = useState<NewTypeHolidaySchedule>(partner.holidaySchedule);
    const [holidaySchedules, setHolidaySchedules] = useState<EditableHoliday[] | undefined>(
        // Make sure to remove all references to original obj to avoid mutating it
        partner.holidaySchedule?.holidays?.map((d) => {
            return { ...d, hasEndDate: d.dtStop > d.dtStart };
        }) || [],
    );

    const [newHolidaySchedules, setNewHolidaySchedules] = useState<EditableHoliday[]>([]);
    const [holidayScheduleName, setHolidayScheduleName] = useState<string>(currentHolidaySchedule?.name || "");
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
    const [isChangesSaved, setIsChangesSaved] = useState(false);

    const handleClose = () => {
        onClose();
    };

    const handleNameChange = (scheduleName: string) => {
        setHolidayScheduleName(scheduleName);
    };

    const handleUpdateHolidaySchedule = (value: Partial<IHoliday>, index: number) => {
        const newArr = [...holidaySchedules];
        newArr[index] = {
            ...newArr[index],
            ...value,
        };

        if (newArr[index].dtStop < newArr[index].dtStart) {
            newArr[index].dtStop = newArr[index].dtStart;
        }

        setHolidaySchedules(newArr);
    };

    const handleDeleteSchedule = (index: number) => {
        const newArr = [...holidaySchedules];
        newArr.splice(index, 1);
        setHolidaySchedules(newArr);
        setIsSaveButtonDisabled(false);
    };

    const addEndDateToHoliday = (index: number) => {
        const newArr = [...holidaySchedules];
        newArr[index].hasEndDate = true;
        setHolidaySchedules(newArr);
        setIsSaveButtonDisabled(false);
    };

    const handleAddNewSchedule = () => {
        const newArr = [
            ...newHolidaySchedules,
            {
                name: "",
                dtStart: moment().utc().startOf("day"),
                dtStop: moment().utc().startOf("day"),
                hasEndDate: false,
            },
        ];
        setNewHolidaySchedules(newArr);
    };

    const handleUpdateNewHolidaySchedule = (value: Partial<IHoliday>, index: number) => {
        const newArr = [...newHolidaySchedules];
        newArr[index] = {
            ...newArr[index],
            ...value,
        };
        setNewHolidaySchedules(newArr);
    };

    const handleDeleteNewSchedule = (index: number) => {
        const holidayArr = [...newHolidaySchedules];
        holidayArr.splice(index, 1);
        setNewHolidaySchedules(holidayArr);
    };

    const addEndDateToNewHoliday = (index: number) => {
        const newArr = [...newHolidaySchedules];
        newArr[index] = {
            ...newArr[index],
            hasEndDate: true,
        };
        setNewHolidaySchedules(newArr);
    };

    const validateHolidayScheduleName = (name: string) => {
        if (!name) {
            return false;
        }
        return true;
    };

    const validateForm = () => {
        const validationErrors = [];

        setErrors(validationErrors);
        return validationErrors.length === 0;
    };

    const handleSave = (event: React.MouseEvent) => {
        event.preventDefault();
        setSaveError(false);

        const valid = validateForm();

        if (valid) {
            newHolidaySchedules.forEach((item, index) => {
                if (item.dtStop < item.dtStart) {
                    newHolidaySchedules[index].dtStop = item.dtStart;
                }
            });

            if (currentHolidaySchedule) {
                const newHolidaySchedulesArr = [...holidaySchedules, ...(newHolidaySchedules as IHoliday[])];

                const newHoliday: IHolidaySchedule = {
                    id: currentHolidaySchedule.id,
                    name: holidayScheduleName || currentHolidaySchedule.name,
                    holidays: newHolidaySchedulesArr as IHoliday[],
                };

                setIsSaveButtonDisabled(true);
                setIsChangesSaved(true);

                partnersRepository.patchPartnerHolidays(partner.id, newHoliday).subscribe(
                    (response) => {
                        if (!response.hasError && response) {
                            props.onHolidayScheduleChange();
                            onClose();
                        }
                    },
                    (error) => {
                        setSaveError(true);
                        console.log(error.error.errors);
                    },
                );
            }

            if (!currentHolidaySchedule) {
                const newHoliday: NewTypeHolidaySchedule = {
                    name: holidayScheduleName,
                    holidays: newHolidaySchedules as IHoliday[],
                };

                setIsSaveButtonDisabled(true);
                setIsChangesSaved(true);

                partnersRepository.createPartnerHolidays(partner.id, newHoliday as IHolidaySchedule).subscribe(
                    (response) => {
                        if (!response.hasError && response) {
                            props.onHolidayScheduleChange();
                            onClose();
                        }
                    },
                    (error) => {
                        setSaveError(true);
                        console.log(error.error.errors);
                    },
                );
            }
        }
    };

    return (
        <div className="dialog card holiday-schedule">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1">{t("Entities.Schedule.HolidaySchedule")}</h1>
            </div>
            {currentHolidaySchedule ? (
                <React.Fragment>
                    <div className="dialog__section dialog__section--full">
                        <p className="paragraph paragraph--bold paragraph--small">{t("Entities.Schedule.Name")}</p>
                        <TextField
                            required
                            fullWidth
                            value={holidayScheduleName}
                            onChange={(e) => {
                                handleNameChange(e.target.value);
                                setIsSaveButtonDisabled(false);
                            }}
                            helperText={
                                errors.includes("holidayScheduleName")
                                    ? t("Validation.IsRequired", {
                                          propertyName: t("Entities.Schedule.Name"),
                                      })
                                    : null
                            }
                            error={errors.includes("holidayScheduleName")}
                            onBlur={(e) => {
                                let validationErrors: string[] = [...errors].filter(
                                    (error) => error !== "holidayScheduleName",
                                );
                                if (
                                    !validateHolidayScheduleName(e.target.value) &&
                                    validationErrors.indexOf("holidayScheduleName") === -1
                                ) {
                                    validationErrors.push("holidayScheduleName");
                                } else {
                                    validationErrors = validationErrors.filter(
                                        (error) => error !== "holidayScheduleName",
                                    );
                                }
                                setErrors(validationErrors);
                            }}
                        />
                    </div>
                    {holidaySchedules.length > 0 ? (
                        <table className="dialog__section dialog__section--full holiday-schedule__table">
                            <tbody>
                                <tr className="dialog__section dialog__section--full holiday-schedule__section holiday-schedule__section--heading">
                                    <th className="holiday-schedule__item">
                                        <p className="caption caption--bold">{t("Entities.Schedule.HolidayName")}</p>
                                    </th>
                                    <th className="holiday-schedule__item">
                                        <p className="caption caption--bold">{t("Entities.Schedule.StartDate")}</p>
                                    </th>
                                    <th className="holiday-schedule__item">
                                        <p className="caption caption--bold">{t("Entities.Schedule.StopDate")}</p>
                                    </th>
                                    <th className="holiday-schedule__item"> </th>
                                </tr>
                                {holidaySchedules.map((holiday, index) => {
                                    if (holiday.dtStop.valueOf() > localNow.valueOf()) {
                                        return (
                                            <tr
                                                className="dialog__section dialog__section--full holiday-schedule__section"
                                                key={index}
                                            >
                                                <th className="holiday-schedule__item">
                                                    <TextField
                                                        required
                                                        value={holiday.name}
                                                        onChange={(e) => {
                                                            handleUpdateHolidaySchedule(
                                                                { name: e.target.value },
                                                                index,
                                                            );
                                                            setIsSaveButtonDisabled(false);
                                                        }}
                                                    />
                                                </th>
                                                <th className="holiday-schedule__item">
                                                    <KeyboardDatePicker
                                                        value={holiday.dtStart}
                                                        disablePast
                                                        autoOk
                                                        onChange={(date) => {
                                                            handleUpdateHolidaySchedule(
                                                                { dtStart: date ? moment(date.toDate()) : null },
                                                                index,
                                                            );
                                                            setIsSaveButtonDisabled(false);
                                                        }}
                                                        allowKeyboardControl
                                                        format="YYYY-MM-DD"
                                                    />
                                                </th>
                                                <th className="holiday-schedule__item">
                                                    {holiday.hasEndDate ? (
                                                        <KeyboardDatePicker
                                                            value={holiday.dtStop}
                                                            disablePast
                                                            autoOk
                                                            minDate={holiday.dtStart}
                                                            onChange={(date) => {
                                                                handleUpdateHolidaySchedule(
                                                                    { dtStop: date ? moment(date.toDate()) : null },
                                                                    index,
                                                                );
                                                                setIsSaveButtonDisabled(false);
                                                            }}
                                                            allowKeyboardControl
                                                            format="YYYY-MM-DD"
                                                        />
                                                    ) : (
                                                        <Button onClick={() => addEndDateToHoliday(index)}>
                                                            {t("Entities.Schedule.AddEndDate")}
                                                        </Button>
                                                    )}
                                                </th>
                                                <th className="holiday-schedule__item">
                                                    <IconButton
                                                        className="icon-button"
                                                        onClick={() => handleDeleteSchedule(index)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} className="icon-button__icon" />
                                                    </IconButton>
                                                </th>
                                            </tr>
                                        );
                                    }
                                    return null;
                                })}
                            </tbody>
                        </table>
                    ) : null}
                </React.Fragment>
            ) : (
                <div className="dialog__section dialog__section--full">
                    <p>{t("Entities.Schedule.Name")}</p>
                    <TextField
                        required
                        fullWidth
                        value={holidayScheduleName}
                        onChange={(e) => {
                            setHolidayScheduleName(e.target.value);
                            setIsSaveButtonDisabled(false);
                        }}
                    />
                </div>
            )}
            <table className="dialog__section dialog__section--full holiday-schedule__table">
                <tbody>
                    {newHolidaySchedules.map((newHoliday, index) => (
                        <tr className="dialog__section dialog__section--full holiday-schedule__section" key={index}>
                            <th className="holiday-schedule__item">
                                <TextField
                                    required
                                    value={newHoliday.name}
                                    onChange={(e) => {
                                        handleUpdateNewHolidaySchedule({ name: e.target.value }, index);
                                        setIsSaveButtonDisabled(false);
                                    }}
                                />
                            </th>
                            <th className="holiday-schedule__item">
                                <KeyboardDatePicker
                                    value={newHoliday.dtStart}
                                    disablePast
                                    autoOk
                                    onChange={(date) => {
                                        handleUpdateNewHolidaySchedule(
                                            {
                                                dtStart: date ? moment(date.toDate()).utc() : null,
                                            },
                                            index,
                                        );
                                        setIsSaveButtonDisabled(false);
                                    }}
                                    allowKeyboardControl
                                    format="YYYY-MM-DD"
                                />
                            </th>
                            <th className="holiday-schedule__item">
                                <KeyboardDatePicker
                                    className={!newHoliday.hasEndDate ? "holiday-schedule__hidden" : null}
                                    value={newHoliday.dtStop}
                                    disablePast
                                    autoOk
                                    minDate={newHoliday.dtStart}
                                    onChange={(date) => {
                                        handleUpdateNewHolidaySchedule(
                                            {
                                                dtStop: date ? moment(date.toDate()).utc() : null,
                                            },
                                            index,
                                        );
                                        setIsSaveButtonDisabled(false);
                                    }}
                                    allowKeyboardControl
                                    format="YYYY-MM-DD"
                                />
                                <Button
                                    className={newHoliday.hasEndDate ? "holiday-schedule__hidden" : null}
                                    onClick={() => addEndDateToNewHoliday(index)}
                                >
                                    {t("Entities.Schedule.AddEndDate")}
                                </Button>
                            </th>
                            <th className="holiday-schedule__item">
                                <IconButton className="icon-button" onClick={() => handleDeleteNewSchedule(index)}>
                                    <FontAwesomeIcon icon={faTrash} className="icon-button__icon" />
                                </IconButton>
                            </th>
                        </tr>
                    ))}
                </tbody>
            </table>

            {saveError ? (
                <div className="dialog__section dialog__section--full">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}
            <div className="dialog__section--buttons holiday-schedule__buttons">
                <Button color="primary" variant="contained" onClick={handleAddNewSchedule}>
                    {t("Gui.Buttons.AddSchedule")}
                </Button>
                <div>
                    <Button onClick={handleClose}>{t("Gui.Buttons.Cancel")}</Button>
                    <Button
                        onClick={(e: React.MouseEvent) => handleSave(e)}
                        variant="contained"
                        color="primary"
                        disabled={isSaveButtonDisabled}
                        startIcon={isChangesSaved ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {t("Gui.Buttons.Save")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
